<template>
  <div class="app" @click="onClick">
    <img v-if="app.icon_uri && !iconFailedToLoad" class="icon" :src="app.icon_uri" @error="iconFailedToLoad = true" />
    <div v-else class="icon icon_stub"></div>

    <div class="content">
      <div class="main-line">
        <div class="name-container">
          <button class="filterable name ellipsis" @click="updateFilters('name')">{{ app.name }}</button>
          <sup class="jit-badge" v-if="app.use_jit_support">JIT</sup>
        </div>
        <span class="version">{{ app.version }}</span>
      </div>

      <div class="source-line ellipsis">
        <button class="filterable ellipsis" @click="updateFilters('developer_name')">{{
          app.developer_name }}</button>

      </div>

      <div class="sub-line">
        <button class="filterable source-link ellipsis" @click="updateFilters('source_name')">{{ app.source_name
        }}</button>

        <div class="metadata">
          <div class="detail-item">
            <button class="filterable ellipsis" @click="updateFilters('type')">{{ $t(app.type?.toUpperCase())
            }}</button>
          </div>
          <div v-if="app.genre_id" class="detail-item">
            <button class="filterable ellipsis" @click="updateFilters('genre_id')">{{ genre }}</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="$scopedSlots['custom-action']" class="actions">
      <slot name="custom-action" :app="app" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { operatingSystems } from '@/data';
import installApp from '@/mixins/installApp';

export default {
  mixins: [installApp],
  props: {
    app: Object,
    shareable: Boolean,
  },
  data() {
    return {
      iconFailedToLoad: false,
      isInstalling: false,
    };
  },
  computed: {
    ...mapState(['genres']),
    genre() {
      return Object.values(this.genres.official)
        .find(genre => genre.id === this.app.genre_id)?.name;
    },
    compatibleOperatingSystems() {
      return operatingSystems.filter(({ name }) => this.app[`min_${name}_version`]);
    },
  },
  methods: {
    updateFilters(filter) {
      if (typeof filter === 'string') {
        this.$emit('update-filters', { [filter]: this.app[filter] });
        return;
      }

      this.$emit('update-filters', filter);
    },
    onClick(e) {
      if (e.target.closest('button')) {
        return;
      }

      this.$router.push(`/details/${this.app.universal_object_identifier}`);
    },
    async install() {
      if (this.app.no_installation_ticket_failure_reason) {
        this.$notify({
          type: 'error',
          text: this.app.no_installation_ticket_failure_reason.translated,
        });
        return;
      }

      this.isInstalling = true;
      try {
        const result = await this.installApp(this.app.object.id, 'universal', { installationTicket: this.app.installation_ticket });
        console.log('[DEBUG] Installation result in UniversalApp.vue:', result);
      } catch (error) {
        console.error('[DEBUG] Installation error in UniversalApp.vue:', error);
      } finally {
        this.isInstalling = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app {
  display: flex;
  gap: 10px;
  padding: 8px;
  border: 1px solid var(--tertiary-accent);
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  height: 100%;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;

  @media (min-width: 768px) {
    gap: 8px;
  }
}

.icon {
  flex: 0 0 45px;
  height: 45px;
  width: 45px;
  border-radius: 22.5%;
  mask: url(../../assets/ios-mask.svg) center/100% 100% no-repeat;
  object-fit: cover;
  object-position: center;

  &_stub {
    background-color: var(--stub-bg);
  }
}

.content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow: hidden;
  width: calc(100% - 55px);
  /* Adjust for icon width + gap */
}

.main-line {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 5px;
  justify-content: flex-start;
}

.name-container {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1 1 auto;
  /* Allow growing and shrinking, but prioritize space for name */
  overflow: hidden;
}

.name {
  font-size: 17px;
  font-weight: 600;
  min-width: 0;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.version {
  color: var(--text-secondary);
  font-size: 14px;
  white-space: nowrap;
  flex: 0 0 auto;
  /* Don't grow or shrink */
  max-width: 30%;
  /* Limit width of version */
  text-overflow: ellipsis;
  overflow: hidden;
}

.source-line {
  display: flex;
  min-width: 0;
  color: var(--text-secondary);
  font-size: 14px;
  max-width: 100%;
}

.source-link {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 100%;
}

.sub-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text-secondary);
  font-size: 14px;
  min-width: 0;
}

.metadata {
  display: flex;
  gap: 5px;
  font-size: 0.85em;
  opacity: 0.7;
  margin-left: auto;
}

.detail-item {
  display: flex;
  gap: 3px;
  white-space: nowrap;
}

.detail-item:not(:last-child)::after {
  content: "•";
  margin-left: 5px;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: stretch;
  white-space: nowrap;
}

.filterable {
  font-size: inherit;
  line-height: inherit;
  color: currentColor;
  text-decoration: none;
  border-bottom: none;

  &:hover {
    text-decoration: underline dotted;
    text-decoration-color: var(--text-secondary);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }
}

.jit-badge {
  background-color: var(--lighter-primary);
  color: var(--primary);
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 2px;
  margin-left: 3px;
  position: relative;
  vertical-align: middle;
  display: inline-block;
}
</style>
